import { useMemo } from 'react';

import { ELoginMethod } from '@webtrader/core/api/user/types';
import { isEmpty } from '@webtrader/core/form/validator';
import { getDialCodeFromCountryKey } from '@webtrader/core/format/country';
import { ILoginCredentialsFormValue } from '@webtrader/core/format/login';
import { isValidEmail, isValidOtp, validatePhoneNumber } from '@webtrader/core/validators';

interface IUseLoginOtpFormHookProps {
  loginMethod: ELoginMethod;
  values: ILoginCredentialsFormValue;
}

export const useLoginOtpFormHook = (props: IUseLoginOtpFormHookProps) => {
  const {
    loginMethod,
    values: { email, phoneNumber, countryKey, verificationCode }
  } = props;
  const dialCode = getDialCodeFromCountryKey(countryKey);

  const isEmailError = useMemo(() => {
    return email ? !isValidEmail(email) : !isEmpty(email);
  }, [email]);

  const isPhoneNumberError = useMemo(() => {
    if (phoneNumber && dialCode) {
      return !validatePhoneNumber(dialCode, phoneNumber);
    }

    if (phoneNumber) {
      return isEmpty(dialCode);
    }

    if (dialCode) {
      return isEmpty(phoneNumber);
    }

    return !isEmpty(dialCode) || !isEmpty(phoneNumber);
  }, [dialCode, phoneNumber]);

  const isVerificationCodeError = useMemo(() => {
    if (verificationCode) {
      return !isValidOtp(verificationCode);
    }

    return !isEmpty(verificationCode);
  }, [verificationCode]);

  const isDisabledVerification = useMemo(() => {
    if (loginMethod === ELoginMethod.EMAIL) {
      return isEmpty(email) || isEmailError;
    }

    return isEmpty(dialCode) || isPhoneNumberError;
  }, [dialCode, email, isEmailError, isPhoneNumberError, loginMethod]);

  const isDisabledSubmit = isDisabledVerification || isEmpty(verificationCode) || isVerificationCodeError;

  return {
    isEmailError,
    isPhoneNumberError,
    isVerificationCodeError,
    isDisabledVerification,
    isDisabledSubmit
  };
};
